import { useEffect, useMemo, useState } from "react";
import { OrgInvitation } from "../../../generated/core-api";
import { jwtDecode } from "jwt-decode";
import { api } from "../../../api";

// Secure in-memory storage for invitation tokens (no localStorage)
const InvitationStore = {
  tokens: new Set<string>(),
  addToken(token: string) {
    this.tokens.add(token);
  },
  removeToken(token: string) {
    this.tokens.delete(token);
  },
  clearTokens() {
    this.tokens.clear();
  },
  getTokens() {
    return Array.from(this.tokens);
  },
};

export function decodeToken(token: string): OrgInvitation | null {
  try {
    const decoded = jwtDecode<OrgInvitation>(token);
    return decoded;
  } catch (e) {
    console.error("Failed to decode invitation token:", e);
    return null;
  }
}

// Function to extract invitation token from URL without storing it
export function extractInvitationTokenFromURL(): string | null {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get("invitation_token");
  } catch (e) {
    console.error("Failed to extract invitation token from URL:", e);
    return null;
  }
}

export function useInvitationToken() {
  // Using state to trigger re-renders when tokens change
  const [updateTrigger, setUpdateTrigger] = useState(0);

  useEffect(() => {
    const token = extractInvitationTokenFromURL();
    if (token) {
      // Add token to in-memory store
      InvitationStore.addToken(token);
      // Force component update
      setUpdateTrigger((prev) => prev + 1);

      // Remove token from URL without reloading (security: don't leave tokens in URL)
      const url = new URL(window.location.href);
      url.searchParams.delete("invitation_token");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- updateTrigger is an intentional invalidation signal for the module-level InvitationStore singleton
  const tokens = useMemo(() => InvitationStore.getTokens(), [updateTrigger]);

  const removeToken = (token: string) => {
    InvitationStore.removeToken(token);
    setUpdateTrigger((prev) => prev + 1);
  };

  const clearTokens = () => {
    InvitationStore.clearTokens();
    setUpdateTrigger((prev) => prev + 1);
  };

  const respondToToken = async (token: string, accept: boolean) => {
    try {
      await api.respondToInvitationByToken({ token, accept });
      removeToken(token);
      return true;
    } catch (error) {
      console.error("Failed to respond to invitation:", error);
      return false;
    }
  };

  return {
    tokens,
    removeToken,
    clearTokens,
    respondToToken,
  };
}
